import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface Pipeline {
  id: string;
  account_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type StageType = 'normal' | 'won' | 'lost';

export interface PipelineStage {
  id: string;
  account_id: string;
  pipeline_id: string;
  name: string;
  order_index: number;
  stage_type: StageType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function usePipelinesSettings() {
  const { user } = useAuth();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipelines = async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('account_pipelines')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setPipelines(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async (pipelineId?: string) => {
    if (!user?.account?.id || !supabase) return;

    try {
      let query = supabase
        .from('account_pipeline_stages')
        .select('*')
        .eq('account_id', user.account.id);

      if (pipelineId) {
        query = query.eq('pipeline_id', pipelineId);
      }

      const { data, error: fetchError } = await query.order('order_index', { ascending: true });

      if (fetchError) throw fetchError;
      setStages(data || []);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const createPipeline = async (pipeline: Omit<Pipeline, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_pipelines')
        .insert({
          account_id: user.account.id,
          ...pipeline
        })
        .select()
        .single();

      if (createError) throw createError;

      if (data) {
        await supabase
          .from('account_pipeline_stages')
          .insert([
            {
              account_id: user.account.id,
              pipeline_id: data.id,
              name: 'Ganhou',
              order_index: 9998,
              stage_type: 'won',
              is_active: true
            },
            {
              account_id: user.account.id,
              pipeline_id: data.id,
              name: 'Perdeu',
              order_index: 9999,
              stage_type: 'lost',
              is_active: true
            }
          ]);
      }

      await fetchPipelines();
      await fetchStages();
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updatePipeline = async (id: string, updates: Partial<Pipeline>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_pipelines')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchPipelines();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deletePipeline = async (id: string) => {
    if (!supabase) return false;

    try {
      await supabase
        .from('account_pipeline_stages')
        .delete()
        .eq('pipeline_id', id);

      const { error: deleteError } = await supabase
        .from('account_pipelines')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchPipelines();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const createStage = async (stage: Omit<PipelineStage, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    const stageType = stage.stage_type || 'normal';
    if (stageType === 'won' || stageType === 'lost') {
      const existingSpecialStage = stages.find(
        s => s.pipeline_id === stage.pipeline_id && s.stage_type === stageType
      );
      if (existingSpecialStage) {
        setError(`Ja existe uma etapa do tipo ${stageType === 'won' ? 'Ganhou' : 'Perdeu'} neste pipeline`);
        return null;
      }
    }

    try {
      const { data, error: createError } = await supabase
        .from('account_pipeline_stages')
        .insert({
          account_id: user.account.id,
          ...stage,
          stage_type: stageType
        })
        .select()
        .single();

      if (createError) throw createError;
      await fetchStages(stage.pipeline_id);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const getStagesByType = (pipelineId: string, stageType: StageType) => {
    return stages.filter(s => s.pipeline_id === pipelineId && s.stage_type === stageType);
  };

  const hasStageType = (pipelineId: string, stageType: StageType, excludeStageId?: string) => {
    return stages.some(s =>
      s.pipeline_id === pipelineId &&
      s.stage_type === stageType &&
      s.id !== excludeStageId
    );
  };

  const updateStage = async (id: string, updates: Partial<PipelineStage>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_pipeline_stages')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchStages();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteStage = async (id: string) => {
    if (!supabase) return false;

    const stageToDelete = stages.find(s => s.id === id);
    if (stageToDelete && (stageToDelete.stage_type === 'won' || stageToDelete.stage_type === 'lost')) {
      setError('Nao e possivel excluir as etapas Ganhou ou Perdeu');
      return false;
    }

    try {
      const { error: deleteError } = await supabase
        .from('account_pipeline_stages')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchStages();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const reorderStages = async (pipelineId: string, stageIds: string[]) => {
    if (!supabase) return false;

    try {
      const updates = stageIds.map((stageId, index) => {
        if (!supabase) return Promise.resolve();
        return supabase
          .from('account_pipeline_stages')
          .update({ order_index: index })
          .eq('id', stageId);
      });

      await Promise.all(updates);
      await fetchStages(pipelineId);
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchPipelines();
    fetchStages();
  }, [user?.account?.id]);

  return {
    pipelines,
    stages,
    loading,
    error,
    fetchPipelines,
    fetchStages,
    createPipeline,
    updatePipeline,
    deletePipeline,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
    getStagesByType,
    hasStageType
  };
}
